import React, {useMemo, useState, useEffect, useRef} from 'react';

const MARKERS = ['A', 'B', 'C', 'D', 'E', 'F'];
const MATCH_COLORS = [
  '#64B5F6',
  '#BA68C8',
  '#FFD54F',
  '#4DD0E1',
  '#FFB74D',
  '#9575CD',
  '#90CAF9',
  '#F48FB1',
];

function shuffleWithSeed(values, seedSource) {
  const seed = Array.from(String(seedSource || ''))
    .reduce((sum, char, index) => sum + char.charCodeAt(0) * (index + 1), 0);

  return [...values]
    .map((value, index) => ({
      value,
      order: (seed + (index + 1) * 7919 + String(value).length * 101) % 100000,
    }))
    .sort((a, b) => a.order - b.order)
    .map(item => item.value);
}

function buildDragDropOptions(question) {
  return Array.from(
    new Set(
      [...(Array.isArray(question.options) ? question.options : []), question.correctAnswer]
        .filter(value => value !== null && value !== undefined && String(value).trim() !== '')
        .map(value => String(value)),
    ),
  );
}

function getOptionFeedbackState(optionValue, selectedValue, correctAnswer, showFeedback) {
  if (!showFeedback) {
    return optionValue === selectedValue ? 'selected' : '';
  }
  if (optionValue === correctAnswer) return 'correct';
  if (optionValue === selectedValue) return 'incorrect';
  return '';
}

const OptionStatus = ({state}) => {
  if (state === 'correct') {
    return <span className="lcv-option-status lcv-option-status-correct">✓</span>;
  }
  if (state === 'incorrect') {
    return <span className="lcv-option-status lcv-option-status-incorrect">✕</span>;
  }
  return null;
};

const MultipleChoice = ({question, onSubmit, showFeedback}) => {
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    setSelected(null);
  }, [question]);

  const handleSubmit = () => {
    if (selected === null) return;
    onSubmit(question.options[selected]);
  };

  return (
    <>
      <div className="lcv-check-question">{question.question}</div>
      <div className="lcv-options">
        {question.options.map((opt, idx) => {
          const state = getOptionFeedbackState(
            opt,
            selected === null ? null : question.options[selected],
            question.correctAnswer,
            showFeedback,
          );

          return (
            <button
              key={idx}
              className={[
                'lcv-option',
                state === 'selected' ? 'lcv-option-selected' : '',
                state === 'correct' ? 'lcv-option-correct' : '',
                state === 'incorrect' ? 'lcv-option-incorrect' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => !showFeedback && setSelected(idx)}
              disabled={showFeedback}
            >
              <span className="lcv-option-marker">{MARKERS[idx]}</span>
              <span className="lcv-option-text">{opt}</span>
              <OptionStatus state={state} />
            </button>
          );
        })}
      </div>
      {!showFeedback && (
        <div style={{display: 'flex', justifyContent: 'flex-end', marginTop: 16}}>
          <button
            className="lcv-btn-primary"
            onClick={handleSubmit}
            disabled={selected === null}
          >
            Submit
          </button>
        </div>
      )}
    </>
  );
};

const FillInTheBlank = ({question, onSubmit, showFeedback}) => {
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    setSelected(null);
  }, [question]);

  const displayQuestion = question.question.replace('______', selected || '______');

  return (
    <>
      <div className="lcv-check-question">{displayQuestion}</div>
      <div className="lcv-word-bank">
        <div className="lcv-wb-label">Word Bank</div>
        <div className="lcv-wb-chips">
          {question.wordBank.map((word, idx) => {
            const state = getOptionFeedbackState(
              word,
              selected,
              question.correctAnswer,
              showFeedback,
            );

            return (
              <button
                key={idx}
                className={[
                  'lcv-wb-chip',
                  state === 'selected' ? 'lcv-wb-chip-selected' : '',
                  state === 'correct' ? 'lcv-wb-chip-correct' : '',
                  state === 'incorrect' ? 'lcv-wb-chip-incorrect' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => !showFeedback && setSelected(word)}
                disabled={showFeedback}
              >
                <span>{word}</span>
                <OptionStatus state={state} />
              </button>
            );
          })}
        </div>
      </div>
      {!showFeedback && (
        <div style={{display: 'flex', justifyContent: 'flex-end', marginTop: 16}}>
          <button
            className="lcv-btn-primary"
            onClick={() => selected && onSubmit(selected)}
            disabled={!selected}
          >
            Submit
          </button>
        </div>
      )}
    </>
  );
};

const Matching = ({question, onSubmit, showFeedback}) => {
  const [matches, setMatches] = useState({});
  const [selectedLeft, setSelectedLeft] = useState(null);
  const [showClickHint, setShowClickHint] = useState(true);
  const [lineGeometry, setLineGeometry] = useState({width: 0, height: 0, lines: []});
  const boardRef = useRef(null);
  const leftRefs = useRef({});
  const rightRefs = useRef({});

  useEffect(() => {
    setMatches({});
    setSelectedLeft(null);
    setShowClickHint(true);
  }, [question]);

  const rightValues = useMemo(
    () =>
      shuffleWithSeed(
        Array.from(new Set((question.matchPairs || []).map(pair => pair.right))),
        `${question.question}-${question.correctAnswer}`,
      ),
    [question.correctAnswer, question.matchPairs, question.question],
  );

  const leftIndexByValue = useMemo(
    () =>
      Object.fromEntries(
        (question.matchPairs || []).map((pair, index) => [pair.left, index]),
      ),
    [question.matchPairs],
  );

  const matchedLeftByRight = useMemo(() => {
    const next = {};
    Object.entries(matches).forEach(([left, right]) => {
      if (right) next[right] = left;
    });
    return next;
  }, [matches]);

  useEffect(() => {
    const updateLines = () => {
      const board = boardRef.current;
      if (!board) return;

      const boardRect = board.getBoundingClientRect();
      const lines = Object.entries(matches)
        .map(([leftValue, rightValue]) => {
          const leftNode = leftRefs.current[leftValue];
          const rightNode = rightRefs.current[rightValue];
          if (!leftNode || !rightNode) return null;

          const leftRect = leftNode.getBoundingClientRect();
          const rightRect = rightNode.getBoundingClientRect();
          const baseColor =
            MATCH_COLORS[leftIndexByValue[leftValue] % MATCH_COLORS.length];
          const pair = (question.matchPairs || []).find(item => item.left === leftValue);
          const isCorrect = pair?.right === rightValue;

          return {
            x1: leftRect.right - boardRect.left,
            y1: leftRect.top + leftRect.height / 2 - boardRect.top,
            x2: rightRect.left - boardRect.left,
            y2: rightRect.top + rightRect.height / 2 - boardRect.top,
            color: showFeedback
              ? isCorrect
                ? '#66BB6A'
                : '#EF5350'
              : baseColor,
          };
        })
        .filter(Boolean);

      setLineGeometry({
        width: boardRect.width,
        height: boardRect.height,
        lines,
      });
    };

    const frame = window.requestAnimationFrame(updateLines);
    window.addEventListener('resize', updateLines);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', updateLines);
    };
  }, [leftIndexByValue, matches, question.matchPairs, showFeedback]);

  const handleLeftClick = leftValue => {
    if (showFeedback) return;
    setShowClickHint(false);
    setSelectedLeft(current => (current === leftValue ? null : leftValue));
  };

  const handleRightClick = rightValue => {
    if (showFeedback || !selectedLeft) return;
    setShowClickHint(false);

    setMatches(previous => {
      const next = {...previous};
      Object.keys(next).forEach(key => {
        if (key !== selectedLeft && next[key] === rightValue) delete next[key];
      });
      next[selectedLeft] = rightValue;
      return next;
    });
    setSelectedLeft(null);
  };

  const allMatched =
    question.matchPairs.length > 0 &&
    question.matchPairs.every(pair => Boolean(matches[pair.left]));

  const handleSubmit = () => {
    const answer = question.matchPairs
      .map(pair => `${pair.left}-${matches[pair.left] || ''}`)
      .join(', ');
    onSubmit(answer);
  };

  return (
    <>
      <div className="lcv-check-question">{question.question}</div>
      <div className="lcv-matching">
        <div className="lcv-matching-hint">
          Match each item on the left with its pair on the right
        </div>
        <div ref={boardRef} className="lcv-match-board">
          <svg
            className="lcv-match-lines"
            width={lineGeometry.width}
            height={lineGeometry.height}
            viewBox={`0 0 ${lineGeometry.width} ${lineGeometry.height}`}
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            {lineGeometry.lines.map((line, index) => (
              <line
                key={index}
                x1={line.x1}
                y1={line.y1}
                x2={line.x2}
                y2={line.y2}
                stroke={line.color}
                strokeWidth="3"
                strokeLinecap="round"
              />
            ))}
          </svg>

          <div className="lcv-match-column lcv-match-column-left">
            {question.matchPairs.map((pair, idx) => {
              const matchedRight = matches[pair.left];
              const pairColor =
                MATCH_COLORS[leftIndexByValue[pair.left] % MATCH_COLORS.length];
              const isCorrect = showFeedback && matchedRight === pair.right;
              const isIncorrect = showFeedback && matchedRight && matchedRight !== pair.right;

              return (
                <button
                  key={pair.left}
                  ref={node => {
                    if (node) leftRefs.current[pair.left] = node;
                  }}
                  className={[
                    'lcv-match-node',
                    selectedLeft === pair.left ? 'lcv-match-node-awaiting' : '',
                    matchedRight && !showFeedback ? 'lcv-match-node-linked' : '',
                    isCorrect ? 'lcv-match-node-correct' : '',
                    isIncorrect ? 'lcv-match-node-incorrect' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  style={
                    matchedRight && !showFeedback
                      ? {
                          '--lcv-match-color': pairColor,
                        }
                      : undefined
                  }
                  onClick={() => handleLeftClick(pair.left)}
                  disabled={showFeedback}
                >
                  <span className="lcv-match-node-badge">{idx + 1}</span>
                  <span>{pair.left}</span>
                  {showClickHint && (
                    <span className="lcv-match-click-hint" aria-hidden="true">
                      ←
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="lcv-match-column lcv-match-column-right">
            {rightValues.map(value => {
              const matchedLeft = matchedLeftByRight[value];
              const pairColor = matchedLeft
                ? MATCH_COLORS[leftIndexByValue[matchedLeft] % MATCH_COLORS.length]
                : null;
              const correctLeft = (question.matchPairs || []).find(
                pair => pair.right === value,
              )?.left;
              const isCorrect = showFeedback && matchedLeft && matchedLeft === correctLeft;
              const isIncorrect = showFeedback && matchedLeft && matchedLeft !== correctLeft;

              return (
                <button
                  key={value}
                  ref={node => {
                    if (node) rightRefs.current[value] = node;
                  }}
                  className={[
                    'lcv-match-node',
                    'lcv-match-node-right',
                    selectedLeft && !matchedLeft && !showFeedback
                      ? 'lcv-match-node-selectable'
                      : '',
                    matchedLeft && !showFeedback ? 'lcv-match-node-linked' : '',
                    isCorrect ? 'lcv-match-node-correct' : '',
                    isIncorrect ? 'lcv-match-node-incorrect' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  style={
                    matchedLeft && !showFeedback && pairColor
                      ? {
                          '--lcv-match-color': pairColor,
                        }
                      : undefined
                  }
                  onClick={() => handleRightClick(value)}
                  disabled={showFeedback || !selectedLeft}
                >
                  <span>{value}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
      {!showFeedback && (
        <div style={{display: 'flex', justifyContent: 'flex-end', marginTop: 16}}>
          <button className="lcv-btn-primary" onClick={handleSubmit} disabled={!allMatched}>
            Submit
          </button>
        </div>
      )}
    </>
  );
};

const DragAndDrop = ({question, onSubmit, showFeedback}) => {
  const [selected, setSelected] = useState(null);
  const dragOptions = useMemo(
    () =>
      shuffleWithSeed(
        buildDragDropOptions(question),
        `${question.question}-${question.correctAnswer}`,
      ),
    [question],
  );

  useEffect(() => {
    setSelected(null);
  }, [question]);

  const state = getOptionFeedbackState(
    selected,
    selected,
    question.correctAnswer,
    showFeedback,
  );

  return (
    <>
      <div className="lcv-check-question">{question.question}</div>
      <div className="lcv-dnd-board">
        <div className="lcv-dnd-drop-label">Drop the answer into the blank</div>
        <div
          className={[
            'lcv-dnd-dropzone',
            selected ? 'lcv-dnd-dropzone-filled' : '',
            state === 'correct' ? 'lcv-dnd-dropzone-correct' : '',
            state === 'incorrect' ? 'lcv-dnd-dropzone-incorrect' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          onDragOver={event => {
            if (!showFeedback) event.preventDefault();
          }}
          onDrop={event => {
            if (showFeedback) return;
            event.preventDefault();
            const value = event.dataTransfer.getData('text/plain');
            if (value) setSelected(value);
          }}
        >
          {selected || '____'}
          <OptionStatus state={state} />
        </div>

        <div className="lcv-dnd-options">
          {dragOptions.map((option, index) => {
            const optionState = getOptionFeedbackState(
              option,
              selected,
              question.correctAnswer,
              showFeedback,
            );

            return (
              <button
                key={index}
                type="button"
                draggable={!showFeedback}
                className={[
                  'lcv-dnd-chip',
                  optionState === 'selected' ? 'lcv-dnd-chip-selected' : '',
                  optionState === 'correct' ? 'lcv-dnd-chip-correct' : '',
                  optionState === 'incorrect' ? 'lcv-dnd-chip-incorrect' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onDragStart={event => {
                  event.dataTransfer.setData('text/plain', option);
                }}
                onClick={() => !showFeedback && setSelected(option)}
                disabled={showFeedback}
              >
                <span>{option}</span>
                <OptionStatus state={optionState} />
              </button>
            );
          })}
        </div>
      </div>
      {!showFeedback && (
        <div style={{display: 'flex', justifyContent: 'flex-end', marginTop: 16}}>
          <button
            className="lcv-btn-primary"
            onClick={() => selected && onSubmit(selected)}
            disabled={!selected}
          >
            Submit
          </button>
        </div>
      )}
    </>
  );
};

const QuestionRenderer = ({question, onSubmit, showFeedback}) => {
  if (!question) return null;

  switch (question.type) {
    case 'multiple_choice':
      return (
        <MultipleChoice
          question={question}
          onSubmit={onSubmit}
          showFeedback={showFeedback}
        />
      );
    case 'fill_in_the_blank':
      return (
        <FillInTheBlank
          question={question}
          onSubmit={onSubmit}
          showFeedback={showFeedback}
        />
      );
    case 'matching':
      return (
        <Matching
          question={question}
          onSubmit={onSubmit}
          showFeedback={showFeedback}
        />
      );
    case 'drag_and_drop':
      return (
        <DragAndDrop
          question={question}
          onSubmit={onSubmit}
          showFeedback={showFeedback}
        />
      );
    default:
      return (
        <div className="lcv-dnd-placeholder">
          Unsupported question type: {question.type}
        </div>
      );
  }
};

export default QuestionRenderer;
